import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Shield, ChevronLeft, ExternalLink, RefreshCw } from "lucide-react";
import { listGroups } from "@/lib/api";

const HealthBadge = ({ score }) => {
    let cls = "text-emerald-300 bg-emerald-500/10 border-emerald-500/20";
    if (score < 90 && score >= 70) cls = "text-cyan-300 bg-cyan-500/10 border-cyan-500/20";
    else if (score < 70 && score >= 40) cls = "text-amber-300 bg-amber-500/10 border-amber-500/20";
    else if (score < 40) cls = "text-rose-300 bg-rose-500/10 border-rose-500/20";
    return (
        <Badge
            variant="outline"
            className={cls}
            data-testid="group-health-score-badge"
        >
            <span dir="ltr" className="font-mono">
                {score}%
            </span>
        </Badge>
    );
};

export default function Groups() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const data = await listGroups();
            const all = data.groups || [];
            // Exclude channels — they have their own page now
            setGroups(all.filter((g) => g.chat_type !== "channel"));
        } catch (_) {
            // ignore network errors; periodic retry handles recovery
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        const i = setInterval(load, 30000);
        return () => clearInterval(i);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-4 md:space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                        المجموعات المحمية
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {groups.length} مجموعة متصلة
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    className="gap-2"
                    data-testid="groups-refresh-button"
                >
                    <RefreshCw className="h-4 w-4" />
                    تحديث
                </Button>
            </div>

            {loading ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            ) : groups.length === 0 ? (
                <Card className="bg-card/40">
                    <CardContent className="p-12 text-center">
                        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/40">
                            <Users className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <h3 className="mt-4 text-lg font-semibold">
                            لا توجد مجموعات بعد
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            أضف Guardian Pro إلى مجموعتك/قناتك لتبدأ الحماية.
                        </p>
                        <Button
                            className="mt-4 gap-2"
                            onClick={() =>
                                window.open(
                                    "https://t.me/cinjago_monitor_bot?startgroup=true",
                                    "_blank",
                                )
                            }
                            data-testid="groups-add-bot-button"
                        >
                            <ExternalLink className="h-4 w-4" />
                            إضافة البوت لمجموعة
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div
                    className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
                    data-testid="groups-grid"
                >
                    {groups.map((g) => (
                        <Card
                            key={g.chat_id}
                            className="bg-card/60 backdrop-blur transition-colors duration-150 hover:bg-card/80 cursor-pointer"
                            onClick={() => navigate(`/groups/${g.chat_id}`)}
                            data-testid={`group-card-${g.chat_id}`}
                        >
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <CardTitle className="text-base truncate">
                                            {g.title}
                                        </CardTitle>
                                        <div className="mt-1 flex items-center gap-2">
                                            <span
                                                className="text-[11px] font-mono text-muted-foreground"
                                                dir="ltr"
                                            >
                                                {g.chat_id}
                                            </span>
                                            <Badge variant="outline" className="text-[10px]">
                                                {g.chat_type}
                                            </Badge>
                                        </div>
                                    </div>
                                    <HealthBadge
                                        score={g.health_score ?? 100}
                                    />
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-xs">
                                        <Shield
                                            className={`h-3.5 w-3.5 ${
                                                g.is_active
                                                    ? "text-emerald-400"
                                                    : "text-muted-foreground"
                                            }`}
                                        />
                                        <span className="text-muted-foreground">
                                            {g.is_active
                                                ? "حماية نشطة"
                                                : "غير نشطة"}
                                        </span>
                                    </div>
                                    <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
